// Shift-drag horizontal scrolling for tables too wide to fit. Mouse events
// only, so touch scrolling is left exactly as it was.
//
// Shift, rather than a plain drag: a table that pans on any press cannot have
// its text selected, and reading a serial number out of a cell is the more
// common thing to want. The modifier also gives the two drag gestures on this
// table one rule between them — Shift-drag a header to move a column (see
// ColumnOrder.js), Shift-drag anywhere else to pan.
//
// The scrolling is the easy half. The hard half is not breaking the table: every
// row is full of links and buttons, and a naive drag handler eats their clicks.
// So nothing is intercepted until the pointer has travelled past a threshold —
// under it the click lands normally — and a press that starts on a control is
// ignored outright, since those own their own gestures.
const DRAG_THRESHOLD_PX = 6;

const INTERACTIVE =
  "a, button, input, select, textarea, label, summary, [role='button'], [contenteditable]";

// A movable column header. Shift-drag there is a reorder, not a pan.
const REORDERABLE = "[data-label]";

export const DragScroll = {
  mounted() {
    this.pending = false;
    this.dragging = false;
    this.swallowClick = false;
    this.armed = false;

    // Shift is the only cue that panning is available, so the grab cursor is
    // bound to holding it rather than to the table merely overflowing.
    this.onKey = (event) => {
      this.armed = event.shiftKey === true;
      this.syncAffordance();
    };

    // A Shift held while switching windows would otherwise leave a grab cursor
    // over a table that no longer pans.
    this.onBlur = () => {
      this.armed = false;
      this.syncAffordance();
    };

    this.onMouseDown = (event) => {
      // Every click is preceded by a mousedown, so clearing here is what keeps a
      // suppression from outliving its gesture. A drag released outside the
      // element gets no click to consume the flag, and the stale flag would then
      // eat an unrelated click later on.
      this.swallowClick = false;

      // Left button only. A middle-click is paste-scroll on some platforms and
      // a right-click opens a menu; neither should start a drag.
      if (event.button !== 0) return;
      // Read from the event rather than the tracked flag: a press can arrive
      // before any keydown does, when the window was focused Shift-first.
      if (!event.shiftKey) return;
      if (event.target.closest(INTERACTIVE)) return;
      // A header owns Shift-drag for reordering; panning the table out from
      // under that would fight the gesture.
      if (event.target.closest(REORDERABLE)) return;
      // Coarse pointers already drag natively, and hijacking touch here would
      // fight the browser's own momentum scrolling.
      if (!window.matchMedia("(pointer: fine)").matches) return;
      if (!this.overflowing()) return;

      // Stops the browser starting a text selection on this press. Doing it
      // here rather than on the first move is the difference between no
      // selection and a selection that is already a few characters long by the
      // time the drag is recognised. Safe because presses on controls have
      // already been excluded above, so nothing loses focus that wanted it.
      event.preventDefault();

      this.startX = event.clientX;
      this.startScrollLeft = this.el.scrollLeft;
      this.pending = true;
    };

    this.onMouseMove = (event) => {
      if (!this.pending) return;

      const dx = event.clientX - this.startX;
      if (!this.dragging && Math.abs(dx) < DRAG_THRESHOLD_PX) return;

      if (!this.dragging) {
        this.dragging = true;
        this.el.classList.add("is-dragging");
        // On the body, not the element: a drag that runs past the table would
        // otherwise select its way across the rest of the page.
        document.body.classList.add("is-drag-scrolling");
        // Anything already highlighted from a previous gesture would grow as
        // the drag moves, so start from nothing.
        window.getSelection()?.removeAllRanges();
      }

      this.el.scrollLeft = this.startScrollLeft - dx;
      // Without this the drag paints a text selection across every row it
      // crosses, which is never what dragging a table means.
      event.preventDefault();
    };

    this.onMouseUp = () => {
      this.pending = false;
      if (!this.dragging) return;

      this.dragging = false;
      this.el.classList.remove("is-dragging");
      document.body.classList.remove("is-drag-scrolling");
      // A mouseup that ends a drag is followed by a click on whatever happens
      // to be under the cursor, which the user never aimed at.
      this.swallowClick = true;
    };

    // Capture phase, so a swallowed click is stopped before the link sees it.
    this.onClickCapture = (event) => {
      if (!this.swallowClick) return;

      this.swallowClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    this.syncAffordance = () => {
      this.el.classList.toggle("is-draggable", this.armed && this.overflowing());
    };

    this.el.addEventListener("mousedown", this.onMouseDown);
    this.el.addEventListener("click", this.onClickCapture, true);
    // On the window, not the element: a drag that leaves the table should keep
    // scrolling, and releasing outside it should still end the drag.
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("resize", this.syncAffordance);
    window.addEventListener("keydown", this.onKey);
    window.addEventListener("keyup", this.onKey);
    window.addEventListener("blur", this.onBlur);

    this.syncAffordance();
  },

  updated() {
    // A column toggle changes the table's width, so whether it can be dragged
    // at all changes with it.
    this.syncAffordance();
  },

  destroyed() {
    // A teardown mid-drag would otherwise leave the page unselectable.
    document.body.classList.remove("is-drag-scrolling");
    this.el.removeEventListener("mousedown", this.onMouseDown);
    this.el.removeEventListener("click", this.onClickCapture, true);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("resize", this.syncAffordance);
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("keyup", this.onKey);
    window.removeEventListener("blur", this.onBlur);
  },

  overflowing() {
    return this.el.scrollWidth > this.el.clientWidth;
  },
};
